import { useCallback, useEffect, useRef, useState } from "react";
import Checkbox from "@ingka/checkbox";
import SSRIcon from "@ingka/ssr-icon";
import pathStarFilled from "@ingka/ssr-icon/paths/star-filled";
import pathStar from "@ingka/ssr-icon/paths/star";
import { useDropdownClamp } from "../hooks/useDropdownClamp";
import styles from "./MultiSelect.module.css";

/** Format a number as compact string: 271700 → "271.7K", 2100 → "2.1K", 500 → "500" */
function formatCompactCount(n: number): string {
    if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return m >= 100 ? `${Math.round(m)}M` : `${+m.toFixed(1)}M`;
    }
    if (n >= 1_000) {
        const k = n / 1_000;
        return k >= 100 ? `${Math.round(k)}K` : `${+k.toFixed(1)}K`;
    }
    return String(n);
}

/** Order-independent comparison of two selections. */
function selectionChanged(a: string[], b: string[]): boolean {
    return a.length !== b.length || a.some((x) => !b.includes(x));
}

interface MultiSelectProps {
    label: string;
    options: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    /** Text to show when all options are selected */
    allSelectedText?: string;
    /** Currently favorited option (stored in localStorage) */
    favorite?: string;
    /** Callback when favorite is toggled */
    onFavoriteChange?: (option: string | null) => void;
    /** Function to format an option for display (e.g., "123 - Store Name") */
    formatOption?: (option: string) => string;
    /** Maximum number of selected items to show in the trigger before ellipsis */
    maxDisplayItems?: number;
    /** Show selected items summary below the dropdown trigger */
    showSelectedSummary?: boolean;
    /** Function to format summary item (defaults to formatOption or identity) */
    formatSummaryItem?: (option: string) => string;
    /** Tooltip text for the label */
    tooltip?: string;
    /** Per-option tooltip text */
    optionTooltips?: Record<string, string>;
    /** Default selection to reset to (shows a "Reset" button when selection differs) */
    defaultSelected?: string[];
    /** Optional receipt counts per option (displayed right-aligned, e.g., "271.7K") */
    counts?: Record<string, number>;
    /** Whether counts are currently being re-fetched (dims stale values) */
    countsLoading?: boolean;
    /** Message shown when the dropdown is disabled due to missing dependencies */
    disabledMessage?: string;
}

export function MultiSelect({
    label,
    options,
    selected,
    onChange,
    placeholder = "Select...",
    allSelectedText = "All selected",
    favorite,
    onFavoriteChange,
    formatOption,
    maxDisplayItems = 2,
    showSelectedSummary = false,
    formatSummaryItem,
    tooltip,
    optionTooltips,
    defaultSelected,
    counts,
    countsLoading = false,
    disabledMessage,
}: MultiSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useDropdownClamp<HTMLDivElement>(isOpen);

    // Draft selection: edits are held locally while the dropdown is open and
    // committed (a single onChange → one data refetch) when it closes. This
    // prevents a request storm when the user toggles many options in a row;
    // previously every checkbox click fired a full set of API calls.
    const [draft, setDraft] = useState<string[]>(selected);

    // While closed, mirror external selection changes (e.g. another filter
    // reset this one). While open, the draft is the user's in-progress edit
    // and must not be clobbered by re-renders.
    useEffect(() => {
        if (!isOpen) setDraft(selected);
    }, [selected, isOpen]);

    // Commit the draft (if changed) and close the dropdown. Used by every
    // close path (trigger toggle, click outside). Reads draft/selected/onChange
    // through refs so the callback identity is stable: the click-outside
    // listener then registers once instead of re-subscribing on every toggle.
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const commitAndClose = useCallback(() => {
        setIsOpen(false);
        if (selectionChanged(draftRef.current, selectedRef.current)) {
            onChangeRef.current(draftRef.current);
        }
    }, []);

    const everythingSelected = draft.length === options.length && options.length > 0;
    // "All selected" text only makes sense with more than one option.
    const allSelected = everythingSelected && options.length > 1;

    const disabled = !!disabledMessage;

    // Build display text for trigger button
    // When showSelectedSummary is true, show count in trigger and details below
    // Otherwise show first few items with ellipsis in trigger
    const displayText = disabled
        ? disabledMessage
        : draft.length === 0
        ? placeholder
        : allSelected
            ? allSelectedText
            : showSelectedSummary
                ? `${draft.length} selected`
                : draft.length <= maxDisplayItems
                    ? draft.map(s => formatOption ? formatOption(s) : s).join(", ")
                    : `${draft.slice(0, maxDisplayItems).map(s => formatOption ? formatOption(s) : s).join(", ")}…`;

    // Summary items to show below (when not all selected)
    const summaryFormatter = formatSummaryItem || formatOption || ((s: string) => s);
    const summaryText = showSelectedSummary && !allSelected && draft.length > 0
        ? (draft.length <= 3
            ? draft.map(summaryFormatter).join(", ")
            : `${draft.slice(0, 2).map(summaryFormatter).join(", ")}… +${draft.length - 2} more`)
        : null;

    // Close dropdown when clicking outside (committing any pending edit)
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                commitAndClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [commitAndClose]);

    const handleToggle = useCallback((option: string, exclusive?: boolean) => {
        if (exclusive) {
            setDraft([option]);
            return;
        }
        setDraft((prev) =>
            prev.includes(option)
                ? prev.filter((s) => s !== option)
                : [...prev, option]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        setDraft([...options]);
    }, [options]);

    // Reset target: explicit defaults > favorite > all options
    const resetTarget = defaultSelected || (favorite ? [favorite] : options);
    // Only show Reset when it differs from Select all (i.e., default is a subset)
    const showReset = resetTarget.length < options.length
        || !resetTarget.every(d => options.includes(d));
    const handleReset = useCallback(() => {
        setDraft([...resetTarget]);
    }, [resetTarget]);

    const isAtDefault = draft.length === resetTarget.length
        && resetTarget.every(d => draft.includes(d));

    const protectedCountry = favorite && draft.includes(favorite) ? favorite : draft[0];


    return (
        <div className={styles.container} ref={containerRef}>
            <label className={styles.label} title={tooltip}>{label}</label>
            <button
                type="button"
                className={`${styles.trigger} ${!disabled && options.length > 0 && draft.length === 0 ? styles.triggerEmpty : ""} ${disabled ? styles.triggerDisabled : ""}`}
                onClick={() => {
                    if (disabled) return;
                    if (isOpen) {
                        commitAndClose();
                    } else {
                        setIsOpen(true);
                    }
                }}
                aria-expanded={isOpen}
                title={disabled ? disabledMessage : draft.length === 0 ? `Select at least one ${label.toLowerCase()}` : undefined}
            >
                <span className={styles.triggerText}>{displayText}</span>
                <span className={styles.arrow}>{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
                <div className={styles.dropdown} ref={dropdownRef}>
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={handleSelectAll}
                            disabled={everythingSelected || options.length === 0}
                        >
                            Select all
                        </button>
                        {showReset && (
                            <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={handleReset}
                                disabled={isAtDefault}
                            >
                                Reset
                            </button>
                        )}
                        <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.doneBtn}`}
                            onClick={commitAndClose}
                        >
                            Done
                        </button>
                    </div>
                    <div className={styles.options}>
                        {options.map((option) => (
                            <div
                                key={option}
                                className={`${styles.option} ${counts && option in counts && counts[option] === 0 ? styles.optionZeroCount : ""}`}
                                onMouseEnter={() => setHoveredOption(option)}
                                onMouseLeave={() => setHoveredOption(null)}
                                title={optionTooltips?.[option]}
                            >
                                <Checkbox
                                    id={`ms-${option}`}
                                    value={option}
                                    checked={draft.includes(option)}
                                    onChange={(e) => {
                                        if (e.nativeEvent instanceof MouseEvent && (e.nativeEvent.metaKey || e.nativeEvent.ctrlKey)) {
                                            e.preventDefault();
                                            return;
                                        }
                                        handleToggle(option);
                                    }}
                                    disabled={draft.length === 1 && option === protectedCountry}
                                    label={formatOption ? formatOption(option) : option}
                                    subtle
                                />
                                <span className={styles.onlyCol}>
                                    {(draft.length > 1 || (draft.length === 1 && !draft.includes(option))) && (
                                        <button
                                            type="button"
                                            className={`${styles.onlyBtn} ${hoveredOption === option ? styles.visible : ""}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleToggle(option, true);
                                            }}
                                            // On touch, act on touchend and preventDefault to suppress the
                                            // emulated click. Selecting "only" collapses the selection and
                                            // reflows the dropdown, so a ghost click would otherwise land on a
                                            // different row and wrongly toggle it.
                                            onTouchEnd={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleToggle(option, true);
                                            }}
                                        >
                                            only
                                        </button>
                                    )}
                                </span>
                                {counts && (
                                    <span className={`${styles.optionCount} ${countsLoading ? styles.optionCountLoading : ""}`}>
                                        {option in counts ? formatCompactCount(counts[option]) : ""}
                                    </span>
                                )}
                                {onFavoriteChange && (
                                    <button
                                        type="button"
                                        className={`${styles.favoriteBtn} ${favorite === option ? styles.favorited : ""} ${hoveredOption === option || favorite === option ? styles.visible : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Toggle favorite - just sets/unsets the star, doesn't change selection
                                            onFavoriteChange(favorite === option ? null : option);
                                        }}
                                        title={favorite === option ? "Remove as default" : "Set as default country"}
                                    >
                                        {favorite === option ? (
                                            <SSRIcon paths={pathStarFilled} className={styles.starIcon} />
                                        ) : (
                                            <SSRIcon paths={pathStar} className={styles.starIcon} />
                                        )}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {summaryText && (
                <span className={styles.summary}>{summaryText}</span>
            )}
        </div>
    );
}
